// The account pages (spec/dashboard.md section 12.4): the profile with the memberships (each organization's role and, per
// game, the caller's game role from `GET /v1/me` → `games`), My activity (the caller's own audit rows through
// `GET /v1/me/audit`, spec/api-conventions.md section 21.1), the personal tokens and the password.
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { AuditEntry, MeGame, Page, PersonalToken } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button, IconButton } from '../ui/Button';
import { Badge } from '../ui/Chip';
import { useConfirm } from '../ui/ConfirmDialog';
import { DataGrid } from '../ui/DataGrid';
import { Field } from '../ui/Field';
import { Activity, Building2, Copy, KeyRound, Lock, Plus, SlidersHorizontal, User, X } from '../ui/icons';
import { Select, TextInput } from '../ui/inputs';
import { Tabs } from '../ui/Tabs';
import { formatInstantWithZone, useTimeZone } from '../ui/time';
import { loadTheme, saveTheme, type ThemeChoice } from '../ui/theme';
import { TimeZonePicker } from '../ui/values';
import { useToasts } from '../ui/Toast';
import { queryCache, useQuery } from '../workspace/cache';
import { routes } from '../shell/routes';
import { AuditPanel } from './settings/AuditPanel';
import { AUDIT_PAGE_SIZE, MY_AUDIT_FILTER_KEYS, orgRoleManages } from './settings/keys';
import { LastUsed } from './settings/KeysPanel';
import './settings/settings.css';
import './org/org.css';
import { LoadError, LoadingPane } from '../ui/PageStates';
import { EmptyState } from '../ui/Banner';

function AccountHead() {
  return (
    <>
      <div className="page-head">
        <h1>Account</h1>
      </div>
      <AccountTabs />
    </>
  );
}

function AccountTabs() {
  return <Tabs ariaLabel="Account" items={[
        { key: 'profile', label: 'Profile', to: routes.account() },
        { key: 'activity', label: 'My activity', to: routes.activity() },
        { key: 'tokens', label: 'Personal tokens', to: routes.tokens() },
        { key: 'password', label: 'Password', to: routes.password() },
      ]} />;
}

export function AccountPage() {
  const { client } = useAuth();
  const me = useQuery('me', () => client.me());
  const [theme, setTheme] = useState<ThemeChoice>(() => loadTheme());
  return (
    <section>
      <AccountHead />
      <div className="account-page">
      {me.loading && !me.data && <LoadingPane rows={3} />}
      {me.data?.user && (
        <div className="pane">
          <div className="pane-head">
            <User className="icon" aria-hidden="true" />
            <h2>Profile</h2>
          </div>
          <div className="pane-body">
            <dl className="kv">
              <dt>Name</dt>
              <dd>{me.data.user.displayName}</dd>
              <dt>Email</dt>
              <dd>{me.data.user.email}</dd>
              <dt>Organizations</dt>
              <dd>{(me.data.memberships ?? []).map((m) => `${m.organization.name} (${m.role})`).join(', ') || '—'}</dd>
            </dl>
          </div>
        </div>
      )}
      {me.data && (
        <div className="pane memberships" data-testid="memberships">
          <div className="pane-head">
            <Building2 className="icon" aria-hidden="true" />
            <h2>Memberships</h2>
          </div>
          <div className="pane-body flush">
          {(me.data.memberships ?? []).length === 0 ? (
            <EmptyState icon={<Building2 />} title={me.data.user ? 'You are not a member of any organization yet.' : 'A service key has no memberships.'} body={me.data.user ? 'Create an organization or accept an invite to join one.' : undefined} />
          ) : (
            <table className="settings-table">
              <thead>
                <tr>
                  <th scope="col">Organization</th>
                  <th scope="col">Slug</th>
                  <th scope="col">Role</th>
                  <th scope="col">Game roles</th>
                </tr>
              </thead>
              <tbody>
                {(me.data.memberships ?? []).map((m) => (
                  <tr key={m.organization.id} data-testid={`membership-${m.organization.slug}`}>
                    <td>
                      <Link to={routes.games(m.organization.slug)}>{m.organization.name}</Link>
                    </td>
                    <td>
                      <code>{m.organization.slug}</code>
                    </td>
                    <td>
                      <Badge tone={m.role === 'owner' ? 'accent' : m.role === 'admin' ? 'info' : 'neutral'}>{m.role}</Badge>
                    </td>
                    <td>
                      <GameRoles orgSlug={m.organization.slug} viaOrg={orgRoleManages(m.role)} games={(me.data!.games ?? []).filter((g) => g.organizationId === m.organization.id)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
          <div className="pane-foot">
            <span className="muted small">Each game's Settings → Members tab manages its roles; organization owners and admins hold every game permission.</span>
          </div>
        </div>
      )}
      <div className="pane">
        <div className="pane-head">
          <SlidersHorizontal className="icon" aria-hidden="true" />
          <h2>Preferences</h2>
        </div>
        <div className="pane-body">
          <div className="form-grid">
            <Field label="Theme" help="Follows the system by default.">
              <Select
                value={theme}
                onChange={(e) => {
                  const v = e.target.value as ThemeChoice;
                  saveTheme(v);
                  setTheme(v);
                }}
              >
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </Select>
            </Field>
            <Field label="Display time zone" help="Every date in the dashboard is shown in this zone, with its label.">
              <TimeZonePicker />
            </Field>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}

/** The caller's game roles in one organization; an owner or admin holds every game through the organization role. */
function GameRoles({ orgSlug, viaOrg, games }: { orgSlug: string; viaOrg: boolean; games: MeGame[] }) {
  if (games.length === 0) return <span className="muted">{viaOrg ? 'every game through the organization role' : 'no game role'}</span>;
  return (
    <>
      {viaOrg && <div className="muted small">every game through the organization role</div>}
      <ul className="settings-game-roles" aria-label={`Game roles in ${orgSlug}`}>
        {games.map((g) => (
          <li key={g.gameId} data-testid={`game-role-${g.gameSlug}`}>
            {g.gameName} <Link to={routes.game(g.organizationSlug, g.gameSlug)}>{g.gameSlug}</Link> <Badge tone={g.role === 'admin' ? 'info' : 'neutral'}>{g.role}</Badge>
            {g.archivedAt && (
              <>
                {' '}
                <Badge tone="warning">archived</Badge>
              </>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * My activity (`/account/activity`): the caller's own audit rows across every organization, newest first, through
 * `GET /v1/me/audit` (no permission needed: the rows are the caller's own), with the filters in the URL. There is no
 * single-row read on this log, so the rows carry no `?entry=` link.
 */
export function ActivityPage() {
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const me = useQuery('me', () => client.me());
  const memberships = me.data?.memberships ?? [];
  const games = me.data?.games ?? [];
  return (
    <section>
      <AccountHead />
      <div data-testid="my-activity">
        {me.data && !me.data.user ? (
          <EmptyState icon={<Activity />} title="No activity of your own" body="A service key has no activity of its own here; read the organization or game audit." />
        ) : (
          <AuditPanel
            cacheKey="me:audit"
            filterKeys={MY_AUDIT_FILTER_KEYS}
            fetchPage={(filters, cursor) => client.data(ops.listMyAudit, { query: { ...filters, limit: AUDIT_PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page<AuditEntry>>}
            when={(v) => formatInstantWithZone(v, zone)}
            organizations={memberships.map((m) => ({ value: m.organization.slug, label: m.organization.name }))}
            orgLabel={(id) => memberships.find((m) => m.organization.id === id)?.organization.slug ?? id}
            gameLabel={(id) => games.find((g) => g.gameId === id)?.gameSlug ?? id}
          />
        )}
      </div>
    </section>
  );
}

export function TokensPage() {
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [zone] = useTimeZone();
  const tokens = useQuery('me:tokens', () => client.data(ops.listPersonalTokens, { query: { limit: 200 } }));
  const [name, setName] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await client.data(ops.createPersonalToken, { body: { name }, idempotencyKey: crypto.randomUUID() });
      setCreated(result.token);
      setName('');
      queryCache.invalidate('me:tokens');
    } catch (err) {
      toasts.error(err instanceof ApiError ? err.message : 'Could not create the token.', { requestId: err instanceof ApiError ? err.requestId : null });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (t: PersonalToken) => {
    if (!(await confirm({ title: `Revoke token ${t.name}?`, body: 'Anything using it stops working at once.', confirmLabel: 'Revoke', tone: 'danger' }))) return;
    try {
      await client.call(ops.revokePersonalToken, { path: { token: t.id }, ifMatch: Number(t.version) });
      queryCache.invalidate('me:tokens');
      toasts.success(`Token ${t.name} revoked.`);
    } catch (err) {
      toasts.error(err instanceof ApiError ? err.message : 'Could not revoke the token.', { requestId: err instanceof ApiError ? err.requestId : null });
    }
  };

  return (
    <section>
      <AccountHead />
      <div className="account-page account-wide">
      <form onSubmit={create} className="pane" aria-label="New personal token">
        <div className="pane-head">
          <KeyRound className="icon" aria-hidden="true" />
          <h2>New personal token</h2>
        </div>
        <div className="pane-body token-create">
          <Field label="Token name" help="What will use it: the CLI, a CI job, an agent.">
            <TextInput aria-label="Token name" placeholder="Token name (what will use it)" value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Button type="submit" variant="primary" icon={<Plus aria-hidden="true" />} busy={busy}>
            Create token
          </Button>
        </div>
      </form>
      {created && (
        <div className="pane pane-highlight" role="status">
          <div className="pane-head">
            <h2>Copy it now; it is shown once</h2>
            <div className="spacer" />
            <IconButton label="Dismiss" onClick={() => setCreated(null)}>
              <X aria-hidden="true" />
            </IconButton>
          </div>
          <div className="pane-body">
            <div className="secret-row">
              <code className="secret-text">{created}</code>
              <Button size="s" icon={<Copy aria-hidden="true" />} onClick={() => void navigator.clipboard?.writeText(created)}>
                Copy
              </Button>
            </div>
          </div>
        </div>
      )}
      <DataGrid
        id="me-tokens"
        rows={tokens.data?.items ?? []}
        rowKey={(t) => t.id}
        loading={tokens.loading}
        emptyState={<EmptyState icon={<KeyRound />} title="No personal tokens yet" body="Create one for the CLI, CI or an agent." />}
        height={400}
        columns={[
          { key: 'name', header: 'Name', fixed: true, render: (t) => t.name },
          { key: 'prefix', header: 'Token', render: (t) => <code>{t.prefix}…{t.hint}</code> },
          { key: 'org', header: 'Organization', render: (t) => (t.organizationId ? <code>{String(t.organizationId).slice(0, 8)}</code> : 'any') },
          { key: 'state', header: 'State', render: (t) => (t.revokedAt ? <Badge tone="danger">revoked</Badge> : t.expiresAt && Date.parse(t.expiresAt) < Date.now() ? <Badge tone="warning">expired</Badge> : <Badge tone="success">active</Badge>) },
          { key: 'used', header: 'Last used', render: (t) => <LastUsed at={t.lastUsedAt} when={(v) => formatInstantWithZone(v, zone)} /> },
          { key: 'actions', header: '', render: (t) => (t.revokedAt ? null : <Button size="s" variant="danger" onClick={() => void revoke(t)}>Revoke</Button>) },
        ]}
      />
      </div>
    </section>
  );
}

export function PasswordPage() {
  const { client } = useAuth();
  const toasts = useToasts();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next !== again) {
      setError(new ApiError(400, 'validation_failed', 'The two new passwords differ.', '', [{ field: 'again', index: null, code: 'invalid_format', message: 'Must equal the new password.' }]));
      return;
    }
    setBusy(true);
    try {
      await client.call(ops.changePassword, { body: { currentPassword: current, newPassword: next } });
      toasts.success('Password changed. Your other sessions were signed out.');
      setCurrent('');
      setNext('');
      setAgain('');
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'internal_error', 'Something went wrong.', ''));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <AccountHead />
      <div className="account-page">
      <form onSubmit={submit} className="pane account-password" aria-label="Change password">
        <div className="pane-head">
          <Lock className="icon" aria-hidden="true" />
          <h2>Change password</h2>
        </div>
        <div className="pane-body">
        <Field label="Current password" required error={error?.fieldMessage('currentPassword')}>
          <TextInput type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </Field>
        <Field label="New password" required error={error?.fieldMessage('newPassword')}>
          <TextInput type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
        </Field>
        <Field label="New password again" required error={error?.fieldMessage('again')}>
          <TextInput type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
        </Field>
        {error && !error.details?.length && (
          <LoadError>{error.message}</LoadError>
        )}
        <p className="muted small">Changing it signs your other sessions out.</p>
        </div>
        <div className="pane-foot">
          <Button type="submit" variant="primary" busy={busy}>
            Change password
          </Button>
        </div>
      </form>
      </div>
    </section>
  );
}
